import React, { useEffect, useState } from 'react';
import { useAgentOrchestration } from '../context/AgentOrchestrationContext';
import { backendService } from '../services/backendService';

interface NavigationProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  gpuLoad?: number;
  compact?: boolean;
}

const Navigation: React.FC<NavigationProps> = ({
  activeTab,
  setActiveTab,
  gpuLoad = 0,
  compact = false
}) => {
  const { mode, visionMode, setMode } = useAgentOrchestration();

  const cycleInteractionMode = () => {
    const modes: Array<'text' | 'voice' | 'vision' | 'multimodal'> = ['text', 'voice', 'vision', 'multimodal'];
    const currentIndex = modes.indexOf(mode as any);
    const nextIndex = (currentIndex + 1) % modes.length;
    setMode(modes[nextIndex]);
  };
  const [pendingTab, setPendingTab] = useState<string | null>(null);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [ollamaMode, setOllamaMode] = useState<'real' | 'fake' | 'omni' | 'hybrid'>('fake');

  const toggleOllamaTarget = async () => {
    const modes: Array<'real' | 'fake' | 'omni' | 'hybrid'> = ['real', 'fake', 'omni', 'hybrid'];
    const currentIndex = modes.indexOf(ollamaMode);
    const newTarget = modes[(currentIndex + 1) % modes.length];
    setOllamaMode(newTarget);
    try {
      await backendService.executeMcpTool('nexus-system', 'ollama_modes', { mode: newTarget });
    } catch (error) {
      console.error('Failed to toggle Ollama mode:', error);
      setOllamaMode(ollamaMode); // revert on failure
    }
  };

  const gpuStatusLabel = gpuLoad > 80
    ? 'GPU_LOAD_CRITICAL'
    : gpuLoad > 50
      ? 'GPU_LOAD_ELEVATED'
      : gpuLoad > 0
        ? 'GPU_LOAD_STABLE'
        : 'GPU_IDLE';

  const items = [
    { id: 'dashboard', icon: 'fa-chart-line', label: 'Dashboard' },
    { id: 'models', icon: 'fa-microchip', label: 'Nodes' },
    { id: 'clients', icon: 'fa-user-shield', label: 'Access' },
    { id: 'settings', icon: 'fa-server', label: 'Kernel' },
    { id: 'video', icon: 'fa-video', label: 'Vision' },
    { id: 'browser', icon: 'fa-globe', label: 'Browser Agent' },
    { id: 'admin-omni', icon: 'fa-cogs', label: 'Admin (Omni)' },
    { id: 'agents', icon: 'fa-network-wired', label: 'Agents' },
    { id: 'skills', icon: 'fa-puzzle-piece', label: 'Skills' },
    { id: 'media', icon: 'fa-photo-video', label: 'Gallery' },
    { id: 'acestep', icon: 'fa-music', label: 'Ace Step' },
    { id: 'mcp', icon: 'fa-cubes', label: 'MCP' },
    { id: 'bridge', icon: 'fa-brain', label: 'Bridge' },
    { id: 'logs', icon: 'fa-terminal', label: 'Live Logs' },
    { id: 'telemetry', icon: 'fa-chart-pie', label: 'Telemetry' },
  ];

  // Debounced tab switching to prevent rapid changes
  const handleTabChange = (tabId: string) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    setPendingTab(tabId);

    const timer = setTimeout(() => {
      setActiveTab(tabId);
      setPendingTab(null);
      setDebounceTimer(null);
    }, 150);

    setDebounceTimer(timer);
  };

  useEffect(() => {
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [debounceTimer]);

  return (
    <nav className={`${compact ? 'w-16' : 'w-60'} bg-[#131722] border-r border-[#2a2e39] flex flex-col h-screen sticky top-0 z-50 transition-all duration-300`}>
      <div className={compact ? 'p-3' : 'p-6'}>
        <div className={`flex items-center ${compact ? 'justify-center' : 'gap-2'} mb-1`}>
          <div className={`${compact ? 'w-9 h-9' : 'w-8 h-8'} bg-purple-600 rounded flex items-center justify-center shadow-lg shadow-purple-900/40`}>
            <i className="fas fa-terminal text-white text-sm"></i>
          </div>
          {!compact && <span className="text-lg font-extrabold tracking-tight text-white">OMNIGATE</span>}
        </div>

        {!compact && (
          <div className="flex items-center gap-1.5 px-1">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
            <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest font-mono">Kernel Core Live</span>
          </div>
        )}
      </div>

      <div className={`flex-1 ${compact ? 'px-1.5' : 'px-3'} space-y-0.5 mt-2`}>
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => handleTabChange(item.id)}
            disabled={pendingTab === item.id}
            title={compact ? item.label : undefined}
            className={`w-full flex items-center ${compact ? 'justify-center' : 'gap-3'} ${compact ? 'px-2 py-3.5' : 'px-4 py-3'} rounded transition-all duration-300 group relative overflow-hidden ${activeTab === item.id
              ? 'bg-[#2a2e39] text-white transform scale-[1.02] shadow-lg'
              : pendingTab === item.id
                ? 'bg-[#1e222d] text-slate-300 opacity-70'
                : 'text-slate-400 hover:bg-[#1e222d] hover:text-slate-200 hover:transform hover:scale-[1.01]'
              }`}
          >
            {item.id === 'video' && visionMode && (
              <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
            )}

            <i className={`fas ${item.icon} w-4 text-xs transition-colors duration-200 ${activeTab === item.id ? 'text-purple-400' : 'text-slate-500 group-hover:text-slate-300'
              }`}></i>

            {!compact && (
              <span className="text-[11px] font-semibold uppercase tracking-wider flex items-center gap-2">
                {item.label}
                {item.id === 'video' && visionMode === 'auto' && (
                  <span className="text-[8px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded font-bold">AUTO</span>
                )}
                {item.id === 'video' && visionMode === 'on-demand' && (
                  <span className="text-[8px] bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded font-bold">ON-DEMAND</span>
                )}
                {item.id === 'video' && visionMode === 'paused' && (
                  <span className="text-[8px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded font-bold">PAUSED</span>
                )}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className={`${compact ? 'p-2' : 'p-4'} mt-auto`}>
        <div className={`bg-[#1e222d] rounded ${compact ? 'p-2' : 'p-3'} border border-[#2a2e39] mb-3`}>
          <div className="flex justify-between items-center mb-2">
            {!compact && <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Interaction Mode</p>}
            <button
              onClick={cycleInteractionMode}
              title="Click to cycle: Text → Voice → Vision → Multimodal"
              className={`text-[9px] font-bold px-2 py-1 rounded cursor-pointer transition-colors hover:opacity-80 ${mode === 'text' ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' :
                mode === 'voice' ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' :
                  mode === 'vision' ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30' :
                    'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                }`}>
              {compact ? mode.charAt(0).toUpperCase() : mode.toUpperCase()}
            </button>
          </div>

          {!compact && (
            <div className="text-[8px] text-slate-400 text-center font-mono">
              {mode === 'text' && 'Text-based interaction'}
              {mode === 'voice' && 'Voice conversation active'}
              {mode === 'vision' && 'Vision monitoring'}
              {mode === 'multimodal' && 'Full multimodal mode'}
            </div>
          )}

          <div className="flex justify-between items-center mt-3 pt-3 border-t border-[#2a2e39]">
            {!compact && <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest pl-1">Ollama Target</p>}
            <button
              onClick={toggleOllamaTarget}
              title="Toggle Ollama routing between Real Engine, Proxy Fake, and Proxy Omni"
              className={`text-[9px] font-bold px-2 py-1 rounded transition-colors shadow-sm ${ollamaMode === 'real'
                ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 shadow-blue-900/20'
                : ollamaMode === 'hybrid'
                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30 shadow-green-900/20'
                  : ollamaMode === 'omni'
                    ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 border border-purple-500/30 shadow-purple-900/20'
                    : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border border-yellow-500/30'
                }`}
            >
              {ollamaMode === 'real' ? 'REAL CORE' : ollamaMode === 'hybrid' ? 'HYBRID (A+M)' : ollamaMode === 'omni' ? 'OMNI MODE' : 'PROXY FAKE'}
            </button>
          </div>
        </div>

        <div className={`bg-[#1e222d] rounded ${compact ? 'p-2' : 'p-3'} border border-[#2a2e39]`}>
          <div className="flex justify-between items-center mb-1.5">
            {!compact && <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">GPU Load</p>}
            <span className={`text-[9px] font-bold ${gpuLoad > 80 ? 'text-red-400' : gpuLoad > 50 ? 'text-yellow-500' : 'text-green-400'}`}>
              {gpuLoad > 0 ? `${Math.round(gpuLoad)}%` : '—'}
            </span>
          </div>

          <div className="h-0.5 bg-[#131722] rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-1000 ${gpuLoad > 80 ? 'bg-red-400' : gpuLoad > 50 ? 'bg-yellow-500' : 'bg-green-400'}`}
              style={{ width: `${Math.min(gpuLoad, 100)}%` }}
            ></div>
          </div>

          {!compact && <p className="text-[8px] text-slate-500 mt-2 font-mono text-center">{gpuStatusLabel}</p>}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
